<template>
  <DialogContainer
    :showDialog="showDialog"
    title="Edit Equipment"
    icon="edit"
    :toolbarActions="[
      {
        text: 'Save',
        icon: 'save',
        buttonColor: 'success',
        emitEvent: 'onSubmitClicked',
        showIsLoading: true,
        isDisabledFunc: () => !isFormValid,
      },
      {
        text: 'Cancel',
        depressed: true,
        emitEvent: 'onCancelClicked',
      },
    ]"
    @onCancelClicked="onCancelClicked"
    @onSubmitClicked="onSubmitClicked"
    :isLoading="isLoading"
  >
    <v-container pa-0 grid-list-lg>
      <v-form v-model="isFormValid" ref="form">
        <v-layout wrap>
          <v-flex xs12 md6>
            <ControlTextField
              label="Equipment Serial/Id*"
              autofocus
              v-model="name"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlAutoComplete
              :items="enabledClientList"
              v-model="clientId"
              item-text="name"
              item-value="id"
              label="Owner*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlAutoComplete
              :items="enabledCategoryList"
              autocomplete
              item-text="name"
              item-value="id"
              v-model="categoryId"
              clearable
              label="Category*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlAutoComplete
              :items="enabledManufacturerList"
              autocomplete
              item-text="name"
              item-value="id"
              v-model="manufacturerId"
              clearable
              label="Manufacturer*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12>
            <ControlAutoComplete
              :items="modelListByCategoryAndManufacturer"
              v-model="modelId"
              item-text="name"
              item-value="id"
              :label="modelLabel"
              :disabled="
                !categoryId ||
                !manufacturerId ||
                !modelListByCategoryAndManufacturer.length ||
                isLoading
              "
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <!-- <v-flex xs12>
          <ControlTextArea
            v-model="comments"
            label="Comments"
            :disabled="isLoading"
          />
        </v-flex> -->
        </v-layout>
        <small>*indicates required field</small>
      </v-form>
    </v-container>
  </DialogContainer>
</template>

<script>
import mixinAssetHelper from '../../../common/mixins/mixinAssetHelper'

import DialogContainer from '../../../common/components/layouts/DialogContainer'
import ControlTextField from '../../../common/components/controls/ControlTextField'
import ControlAutoComplete from '../../../common/components/controls/ControlAutoComplete'
import ControlCheckbox from '../../../common/components/controls/ControlCheckbox'
import ControlTextArea from '../../../common/components/controls/ControlTextArea'

export default {
  components: {
    DialogContainer,
    ControlTextField,
    ControlAutoComplete,
    ControlCheckbox,
    ControlTextArea,
  },
  mixins: [mixinAssetHelper],
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    assetDetail: {
      type: Object,
      required: true,
    },
    modelList: {
      type: Array,
      required: true,
    },
    manufacturerList: {
      type: Array,
      required: true,
    },
    categoryList: {
      type: Array,
      required: true,
    },
    locationList: {
      type: Array,
      required: true,
    },
    clientList: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    isFormValid: false,
    id: null,
    name: null,
    categoryId: null,
    manufacturerId: null,
    modelId: null,
    clientId: null,
    comments: null,
  }),
  computed: {
    modelLabel() {
      return this.categoryId &&
        this.manufacturerId &&
        !this.modelListByCategoryAndManufacturer.length
        ? `No models found for ${this._entityPropertyValueByName({
          entityList: this.categoryList,
          idKey: 'id',
          idValue: this.categoryId,
          propertyKey: 'name',
        })} and ${this._entityPropertyValueByName({
          entityList: this.manufacturerList,
          idKey: 'id',
          idValue: this.manufacturerId,
          propertyKey: 'name',
        })}`
        : 'Model*'
    },
    modelListByCategoryAndManufacturer() {
      return this._modelsByCategoryIdAndManufacturerId({
        modelList: this.modelList,
        categoryId: this.categoryId,
        manufacturerId: this.manufacturerId,
      })
    },
    enabledClientList() {
      return this.clientList.filter((client) => client.isEnabled)
    },
    enabledCategoryList() {
      return this.categoryList.filter((category) => category.isEnabled)
    },
    enabledManufacturerList() {
      return this.manufacturerList.filter(
        (manufacturer) => manufacturer.isEnabled
      )
    },
  },
  watch: {
    modelListByCategoryAndManufacturer() {
      if (
        !this.modelListByCategoryAndManufacturer.some(
          (model) => model.id === this.modelId
        )
      ) {
        this.modelId = null
      }
    },
    showDialog() {
      if (this.showDialog && this.assetDetail) {
        this.id = this.assetDetail.id
        this.name = this.assetDetail.name
        this.modelId = this.assetDetail.modelId
        this.comments = this.assetDetail.comments
        this.clientId = this.assetDetail.clientId
        let model = this._entityById({
          entityList: this.modelList,
          idKey: 'id',
          idValue: this.modelId,
        })
        if (model) {
          let category = this._entityById({
            entityList: this.categoryList,
            idKey: 'id',
            idValue: model.categoryId,
          })
          if (category) {
            this.categoryId = category.id
          }
          let manufacturer = this._entityById({
            entityList: this.manufacturerList,
            idKey: 'id',
            idValue: model.manufacturerId,
          })
          if (manufacturer) {
            this.manufacturerId = manufacturer.id
          }
        }
      }
      if (!this.showDialog) {
        this.clearDialog()
      }
    },
  },
  methods: {
    onSubmitClicked() {
      this.$emit('onSubmitClicked', {
        id: this.id,
        name: this.name,
        categoryId: this.categoryId,
        manufacturerId: this.manufacturerId,
        modelId: this.modelId,
        clientId: this.clientId,
        comments: this.comments,
      })
    },
    onCancelClicked() {
      this.$emit('onCancelClicked')
    },
    clearDialog() {
      this.$refs.form.reset()
      this.id = null
      this.name = null
      this.categoryId = null
      this.clientId = null
      this.manufacturerId = null
      this.modelId = null
      this.comments = null
      this.assetCount = 0
    },
  },
}
</script>